import {chain} from 'lodash';
import {createSelector} from 'reselect';

import {
  isAccountDomainAssociationJob,
  isAccountsCsvImportJob,
  isBackfillCustomViewJob,
  isCalendarEventImportJob,
  isChannelImportJob,
  isContactsCsvExportJob,
  isContactsCsvImportJob,
  isInboxMassUpdateJob,
  isJobFailed,
  isJobRunning,
  isSelectAllJob,
  isSyncAccountsJob,
  isSyncCardsJob,
  isSyncJob,
} from '../../../helpers/jobHelpers';
import {AppState} from '../../appState';
import {getUserOwnedChannels, getUserParticipatingChannels} from '../channels/channelsSelectors';
import {getCalendarVersions} from '../dataSelectors';
import {getCustomViewTagIds} from '../tags/tagsSelectors';
import {getUser, getUserId} from '../teammates/teammatesSelectors';

/*
 * Common job selectors.
 */

export function getJobs(state: AppState) {
  return state.data.jobs;
}

const getFlatJobs = createSelector(getJobs, (jobs) =>
  chain(jobs)
    .values()
    .compact()
    .sortBy((j) => j.id)
    .value(),
);

/*
 * Mass archive job selectors.
 */

export const getUserMassArchiveJobs = createSelector(
  [getFlatJobs, getUserOwnedChannels],
  (flatJobs, userOwnedChannels) =>
    chain(flatJobs)
      .map((job) => {
        if (!isInboxMassUpdateJob(job)) {
          return undefined;
        }

        // Check that this inbox resource contains a single owned channel.
        const inboxChannels = userOwnedChannels.filter((c) => c.inboxId === job.inboxId);
        if (inboxChannels.length !== 1) {
          return undefined;
        }

        return job;
      })
      .compact()
      .value(),
);

/*
 * Select all job selectors.
 */

export const getSelectAllJobs = createSelector([getFlatJobs], (flatJobs) => flatJobs.filter(isSelectAllJob));

export const getIsSomeSelectAllJobRunning = createSelector(getSelectAllJobs, (jobs) =>
  jobs.some(isJobRunning),
);

/*
 * Channel import job selectors.
 */

export const getUserChannelImportJobs = createSelector(
  [getFlatJobs, getUserParticipatingChannels],
  (flatJobs, userParticipatingChannels) =>
    chain(flatJobs)
      .map((job) => {
        if (!isJobRunning(job) || !isChannelImportJob(job)) {
          return undefined;
        }

        // Check if this job is for one of the channels our user owns.
        if (!userParticipatingChannels.some((c) => c.id === job.channelId)) {
          return undefined;
        }

        return job;
      })
      .compact()
      .value(),
);

export const getChannelIdsFromImportJobs = createSelector(getUserChannelImportJobs, (userChannelImportJobs) =>
  userChannelImportJobs.map((job) => job.channelId),
);

/*
 * Calendar import job selectors.
 */

export const getCalendarVersionIdsFromImportJobs = createSelector(getFlatJobs, (flatJobs) =>
  chain(flatJobs)
    .filter(isJobRunning)
    .filter(isCalendarEventImportJob)
    .map((job) => job.calendarVersionId)
    .value(),
);

export const getIsImportingUserCalendarVersions = createSelector(
  [getCalendarVersionIdsFromImportJobs, getCalendarVersions],
  (calendarVersionIds, calendarVersions) => calendarVersionIds.some((id) => Boolean(calendarVersions[id])),
);

/*
 * Custom views job selectors.
 */

// All custom view tags that are pinned by the current user. Used to display the loader for those tags.
export const getCustomViewBackfillJobs = createSelector(
  [getFlatJobs, getCustomViewTagIds],
  (flatJobs, customViewTags) =>
    chain(flatJobs)
      .filter(isJobRunning)
      .filter(isBackfillCustomViewJob)
      .filter((job) => customViewTags.includes(job.tagId))
      .value(),
);

// All custom view jobs initiated by the current user. Used to display the banner with the backfill progress.
// Other users who have the custom view pinned, but have not initiated the creation should not see the banner.
export const getUserCustomViewBackfillJobs = createSelector(
  [getUserId, getCustomViewBackfillJobs],
  (userId, customViewJobs) => customViewJobs.filter((job) => job.requesterId === userId),
);

export const getIsBackfillingCustomView = createSelector(
  [getCustomViewBackfillJobs],
  (customViewJobs) => customViewJobs.length > 0,
);

/*
 * Accounts Import job selectors.
 */

export const getAccountsRunningImportJobs = createSelector(getFlatJobs, (flatJobs) =>
  chain(flatJobs).filter(isJobRunning).filter(isAccountsCsvImportJob).value(),
);

export const getAccountsErredImportJobs = createSelector(getFlatJobs, (flatJobs) =>
  chain(flatJobs).filter(isJobFailed).filter(isAccountsCsvImportJob).value(),
);

export const getAccountsImportJobs = createSelector(getFlatJobs, (flatJobs) =>
  chain(flatJobs).filter(isAccountsCsvImportJob).value(),
);

/*
 * Import Contacts CSV job selectors.
 */

export const getImportContactsCsvJobs = createSelector(getFlatJobs, (flatJobs) =>
  chain(flatJobs).filter(isContactsCsvImportJob).value(),
);

export const getRunningImportContactsCsvJobs = createSelector(getImportContactsCsvJobs, (flatJobs) =>
  chain(flatJobs).filter(isJobRunning).value(),
);

export const getFailedImportContactsCsvJobs = createSelector(getImportContactsCsvJobs, (flatJobs) =>
  chain(flatJobs).filter(isJobFailed).value(),
);

/*
 * Export Contacts CSV job selectors.
 */

export const getExportContactsCsvJobs = createSelector(getFlatJobs, (flatJobs) =>
  chain(flatJobs).filter(isContactsCsvExportJob).value(),
);

/*
 * Account Domain Association job selectors.
 */

export const getAccountDomainAssociationJobs = createSelector(getFlatJobs, (flatJobs) =>
  chain(flatJobs).filter(isAccountDomainAssociationJob).value(),
);

/*
 * Crm Sync Accounts Job Selectors.
 */

export const getAllSyncAccountsJobs = createSelector(getFlatJobs, (flatJobs) =>
  chain(flatJobs).filter(isSyncAccountsJob).value(),
);

export const getAllSyncCardsJobs = createSelector(getFlatJobs, (flatJobs) =>
  chain(flatJobs).filter(isSyncCardsJob).value(),
);

export const getAllRunningSyncAccountsJobs = createSelector(getAllSyncAccountsJobs, (flatJobs) =>
  chain(flatJobs).filter(isJobRunning).value(),
);

export const getAllRunningSyncCardsJobs = createSelector(getAllSyncCardsJobs, (flatJobs) =>
  chain(flatJobs).filter(isJobRunning).value(),
);

export const getUsersSyncJobs = createSelector([getFlatJobs, getUser], (flatJobs, user) =>
  chain(flatJobs)
    .filter(isSyncJob)
    .filter((job) => job.requesterId === user.id)
    .value(),
);
